const surface = require('../surface/surface');

// The body-level popupEdit/popupIframe/confirm host. Its <form> comes from the leaf
// theme's zit-modal template and is a sibling of the page's mainForm, never nested — so
// the components it hosts are scoped to it by plain DOM ancestry. See surface.js for the
// contract this implements.
const modal = Object.assign({}, surface, {
    // Structural subelements discovered inside the leaf-provided modal
    // template by their data-zit-subelement-type markers. The leaf owns
    // the markup (Bootstrap-shaped under metronic_8); base JS reaches
    // in via these neutral markers instead of hardcoded class selectors.
    subelements: {
        dialog: null,
        header: null,
        title: null,
        body: null,
        footer: null
    },
    // Action zones the leaf template exposes via data-zit-actions-position.
    positions: ['footerLeft', 'footerRight', 'headerRight'],
    // Zone actions land in when addActions() is called without a position.
    // Switches to headerRight when the modal is built without a footer.
    defaultPosition: 'footerRight',
    modalObj: false,

    // options: { noHeader, noFooter } — when set, the corresponding structural
    // slot is removed from the template before the modal is attached to the body.
    init: function (options) {
        options = options || {};

        let modalHTML = this.hm.helper.getTemplate('zit-modal');
        this.element = this.hm.helper.string2element(modalHTML);

        // Discover the structural subelements within the rendered template.
        // The standard initSubelements() walks the document for
        // data-zit-subelement-for=<id>, but the modal element is dynamically
        // created and not yet attached to a component-id scope — query the
        // element subtree directly by data-zit-subelement-type. (Querying works
        // on the still-detached element, so header/footer can be stripped before
        // it is appended below.)
        this.subelements.dialog = this.element.querySelector('[data-zit-subelement-type="dialog"]');
        this.subelements.header = this.element.querySelector('[data-zit-subelement-type="header"]');
        this.subelements.title = this.element.querySelector('[data-zit-subelement-type="title"]');
        this.subelements.body = this.element.querySelector('[data-zit-subelement-type="body"]');
        this.subelements.footer = this.element.querySelector('[data-zit-subelement-type="footer"]');

        // The modal template wraps header/body/footer in its own <form>: that is the form
        // the hosted components belong to, by ancestry.
        this.formElement = this.element.querySelector('form');

        // Optionally strip the header and/or footer before attach. The title
        // lives inside the header, so it goes with it.
        if (options.noHeader && this.subelements.header !== null) {
            this.subelements.header.remove();
            this.subelements.header = null;
            this.subelements.title = null;
        }

        if (options.noFooter && this.subelements.footer !== null) {
            this.subelements.footer.remove();
            this.subelements.footer = null;
            this.defaultPosition = 'headerRight';
        }

        document.body.append(this.element);

        // Construct the modal via the theme's behaviour adapter (the contract
        // returns the same { show, hide, dispose } surface bootstrap.Modal
        // exposes today). bootstrap5 wraps bootstrap.Modal directly;
        // metronic_8 inherits unchanged.
        this.modalObj = this.hm.behaviors.modal.create(this.element);
    },

    setSize: function (size) {
        ['modal-lg', 'modal-sm', 'modal-xl', 'modal-fullscreen', 'modal-fullcontent'].forEach((alreadySize) => {
            if (this.subelements.dialog.classList.contains(alreadySize)) {
                this.subelements.dialog.classList.remove(alreadySize);
            }
        });

        switch (size) {
            case 'lg':
            case 'large':
                size = 'modal-lg';
                break;

            case 'sm':
            case 'small':
                size = 'modal-sm';
                break;

            case 'xl':
            case 'extra':
                size = 'modal-xl';
                break;

            // Covers the entire browser viewport, menus included. Bootstrap ships
            // .modal-fullscreen (so metronic_8 needs nothing); cleanSlate defines its own.
            case 'fullScreen':
            case 'fullscreen':
                size = 'modal-fullscreen';
                break;

            // Covers only the content area: the theme's main menus stay visible and usable.
            // Each theme decides what that means — it is the one that knows where its own
            // chrome sits (metronic_8: below the fixed header; cleanSlate: beside the sidebar
            // or below the top bar, per menuPosition) — so both define .modal-fullcontent in
            // their style/index.css.
            case 'fullContent':
            case 'fullcontent':
                size = 'modal-fullcontent';
                break;

            default:
                size = false;
        }

        if (size !== false) {
            this.subelements.dialog.classList.add(size);
        }
    },

    // A named zone the leaf template does not provide falls back to the footer element
    // (null on a footer-less modal).
    defaultZoneElement: function () {
        return this.subelements.footer;
    },

    open: function () {
        if (this.modalObj !== false) {
            this.modalObj.show();
        }
    },

    close: function () {
        if (this.modalObj !== false) {
            this.modalObj.hide();
        }
    },

    dispose: function () {
        if (this.modalObj !== false) {
            this.modalObj.dispose();
            this.modalObj = false;
            this.element.remove();
        }

        this.disposed = true;
    }
});

module.exports = modal;
